import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DataTable, {
  PAGE_SIZES,
} from "../../components/carbon/DataTable/DataTable";
import PageHeader from "../../components/carbon/PageHeader/PageHeader";
import EmptyState from "../../components/carbon/EmptyState/EmptyState";
import { NetworkError } from "../../components";
import OrderStateFilter, { OrderStateOption } from "../Orders/OrderStateFilter";
import {
  buildOrderRows,
  navigateToSellerBidding,
  orderExpandedContent,
  supplierBiddingHeaders,
} from "../Orders/orderListRows";
import useDebounce from "../../hooks/useDebounce";
import { useSupplierBiddings } from "../../hooks/useOrders";
import { OrderState } from "../../types";
import { BIDDING_LABELS } from "../../utils";
import "../Orders/orders.scss";

/**
 * Seller "RFQs / biddings" screen (`/biddings`) — one unified, server-paginated Carbon `DataTable`
 * of every RFQ the seller was invited to, across ALL states (filterable) and including ones already
 * quoted, reusing the same expandable rows as the seller home. Replaces the previous two-tab shell
 * (a pair of OPEN-only opportunity tables + a separate "Offers made" tab). A row click routes to the
 * seller bid view when already quoted, or the submission page otherwise (see `navigateToSellerBidding`).
 */
const BiddingOpportunities = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[0]);
  const [searchTerm, setSearchTerm] = useState("");
  const { debouncedSearchTerm } = useDebounce(searchTerm, 500);
  const [selectedStates, setSelectedStates] = useState<OrderState[]>([]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearchTerm, selectedStates]);

  const apiParams = useMemo(
    () => ({
      page,
      size: pageSize,
      ...(debouncedSearchTerm ? { search: debouncedSearchTerm } : {}),
      ...(selectedStates.length > 0 ? { states: selectedStates } : {}),
      sortBy: "finishDate",
      sortDirection: "DESC" as const,
    }),
    [page, pageSize, debouncedSearchTerm, selectedStates]
  );

  const { data, isLoading, error } = useSupplierBiddings(apiParams);
  const rows = useMemo(() => buildOrderRows(data, t, true), [data, t]);
  const headers = useMemo(() => supplierBiddingHeaders(t), [t]);

  // Suppliers never see SCHEDULED (PENDING) or CANCELLED orders, so those states aren't offered as
  // filter options (the backend also excludes them defensively).
  const stateOptions: OrderStateOption[] = useMemo(
    () => [
      { value: OrderState.OPEN, label: BIDDING_LABELS.IN_PROGRESS },
      { value: OrderState.SUCCEED, label: BIDDING_LABELS.SUCCESS },
      { value: OrderState.CLOSED, label: BIDDING_LABELS.CLOSED },
    ],
    []
  );

  return (
    <div>
      <PageHeader title={t("navigation.tenders")} />

      <div className="orders__filters">
        <OrderStateFilter
          idPrefix="biddings"
          options={stateOptions}
          selected={selectedStates}
          onChange={setSelectedStates}
        />
      </div>

      {error ? (
        <NetworkError />
      ) : (
        <DataTable
          headers={headers}
          rows={rows}
          isLoading={isLoading}
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            label: t("cta.search"),
          }}
          pagination={{
            page: page + 1,
            pageSize,
            pageSizes: PAGE_SIZES,
            totalItems: data?.totalElements ?? 0,
            onPageChange: (newPage, newPageSize) => {
              setPage(newPage - 1);
              setPageSize(newPageSize);
            },
          }}
          onRowClick={(row) => navigateToSellerBidding(navigate, row)}
          getExpandedContent={(row) => orderExpandedContent(row, t)}
          empty={<EmptyState title={t("messages.tendersSoon")} />}
        />
      )}
    </div>
  );
};

export default BiddingOpportunities;
